import { useState, useLayoutEffect } from 'react';
import Button from '../../atoms/button';
import Input from '../../atoms/input';
import Card from '../../atoms/card';
import classNames from 'classnames';
import * as XLSX from 'xlsx';
import { dateObjToStr } from '../../../utils/date';
import { useTranslation } from 'react-i18next';
import { MonitoringForm } from './containers';
import MonitoringService, {
	IMonitoring,
} from '../../../services/Monitoring.service';
import { NotificationToast } from '../notification-toast';
import Modal from '../modal';
import SVGIcon from '../../atoms/icons';
import Text from '../../atoms/text';
import { ClipLoader } from 'react-spinners';
import { RetroactiveMonitoringForm } from './retroactive-monitoring';
import { IYard } from '../../../services/Yard.service';
import { GeneralProblemForm } from './general-problem';

interface SearchBarProps {
	data: MonitoringForm[];
	generalProblemData: GeneralProblemForm;
	retroactiveData: RetroactiveMonitoringForm;
	search: string;
	setSearch: React.Dispatch<React.SetStateAction<string>>;
	monitorings: MonitoringForm[];
	setMonitorings: React.Dispatch<React.SetStateAction<MonitoringForm[]>>;
	yardSelected?: IYard;
}

const SearchBar = ({
	data,
	generalProblemData,
	retroactiveData,
	search,
	setSearch,
	monitorings,
	setMonitorings,
	yardSelected,
}: SearchBarProps) => {
	const { t } = useTranslation();

	const [isAtTop, setIsAtTop] = useState(false);
	const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [modalAlert, setModalAlert] = useState<string>();
	const [modalDescription, setModalDescription] = useState<string>();

	useLayoutEffect(() => {
		const handleScroll = () => {
			const shouldBeAtTop = window.scrollY > 100;
			setIsAtTop(shouldBeAtTop);
		};

		handleScroll();

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, [data]);

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) =>
		setSearch(e.target.value);

	const selecionarTodos = () =>
		setMonitorings((prev) =>
			prev.map((item) => ({ ...item, isChecked: true }))
		);

	const handleExport = () => {
		const dataSheet = data.map((item) => [item.label, item.temperature]);

		const ws = XLSX.utils.aoa_to_sheet([
			[
				t('port_management.container'),
				t('port_management.containers.temperature'),
			],
			...dataSheet,
		]);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1');

		XLSX.writeFile(
			wb,
			`reefersys-monitoring-${dateObjToStr(new Date())}.xlsx`
		);
	};

	const handleSave = async (message?: string) => {
		const dataFiltered = monitorings.filter((item) => item.isChecked);
		if (dataFiltered.length == 0) {
			handleModalVisibility(
				true,
				t('monitor.alert_none'),
				t('monitor.description_none')
			);
			return;
		}
		setIsLoading(true);
		const data: IMonitoring[] = dataFiltered.map((item) => {
			const problems: number[] = [];
			if (item.tempStatus === 'Coolingdown') {
				problems.push(2);
			} else if (item.tempStatus === 'Defrost') {
				problems.push(3);
			} else if (item.cntrStatus === 'Temperatura Crítica') {
				problems.push(13);
			} else if (item.cntrStatus === 'Temperatura em Alerta') {
				problems.push(12);
			}

			if (item.cntrStatus === 'Problema Crítico') {
				problems.push(4);
			} else if (item.cntrStatus === 'Sem Alimentação') {
				problems.push(6);
			} else if (item.cntrStatus === 'Falta de Energia') {
				problems.push(8);
			}

			if (item.reparoPreventivo) {
				problems.push(10);
			}

			if (generalProblemData.isProblem) {
				problems.splice(0, problems.length, 8, 88);
			}

			return {
				temperature: item.setPoint === null ? null : Number(item.temperature),
				imgFiles: item.files,
				observation: item.observation,
				id_container: item.idContainer,
				humidity: item.humidity,
				ventilation: item.ventilation,
				atmosferaControlada: item.atmosferaControlada,
				nivelCo2: item.nivelCo2,
				return: item.return == null ? undefined : Number(item.return),
				supply: item.supply == null ? undefined : Number(item.supply),
				problems,
				date: retroactiveData.date,
			};
		});

		const response = await MonitoringService.create(data);
		setIsLoading(false);

		if (!response) return;
		NotificationToast.Success(
			`${t('monitor.monitorings')} ${t(message || 'alert.success.save')}`
		);

		handleSaveLocal();
	};

	const handleModalVisibility = (
		visible: boolean,
		alert?: string,
		description?: string
	) => {
		setModalAlert(alert);
		setModalDescription(description);
		setIsModalVisible(visible);
	};

	const handleSaveLocal = () => {
		const dataFiltered = monitorings.filter((item) => item.isChecked);
		if (dataFiltered.length == 0) {
			handleModalVisibility(
				true,
				t('monitor.alert_none'),
				t('monitor.description_none')
			);
			return;
		}

		if (yardSelected) {
			const idsMonitorados = new Set(
				dataFiltered.map((item) => item.idContainer)
			);
			const dataLocal = dataFiltered.map((item) => ({
				...item,
				possuiMonitoramentoLocal: true,
			}));

			localStorage.setItem(
				`monitoring-yard-${yardSelected.id}`,
				JSON.stringify({ data: dataLocal })
			);
			setMonitorings((prev) =>
				prev.map((item) => ({
					...item,
					possuiMonitoramentoLocal: idsMonitorados.has(item.idContainer)
						? true
						: item.possuiMonitoramentoLocal,
				}))
			);
			NotificationToast.Success(t('monitor.save_local'));
		}
	};

	return (
		<>
			<Card
				className={classNames(
					'lg:fixed lg:z-10 w-full left-0 grid grid-rows-[auto] grid-flow-row sm:grid-cols-[auto] sm:grid-flow-col justify-end py-3 pr-12 gap-3 transition-all duration-300',
					{
						'lg:top-0': isAtTop,
						'lg:bottom-0': !isAtTop,
						hidden: data.length == 0,
					}
				)}
			>
				<Input
					className="w-64"
					placeholder="Pesquisar"
					icon="Search"
					value={search}
					onChange={handleSearch}
				/>
				<Button
					className="w-fit"
					disabled={monitorings.every((item) => item.isChecked)}
					onClick={selecionarTodos}
				>
					Selecionar Todos
				</Button>
				<Button
					className="w-fit stroke-white"
					icon="Download"
					onClick={handleExport}
				>
					Exportar Planilha
				</Button>
				<Button
					className="w-fit"
					icon="Save"
					onClick={() => handleSaveLocal()}
				>
					Salvar
				</Button>
				<Button
					className="w-fit"
					icon="Share"
					onClick={() => handleSave()}
				>
					Salvar e Enviar
				</Button>
			</Card>
			<Modal
				className="flex flex-col items-center justify-center gap-4"
				isModalVisible={isModalVisible}
				onClose={() => handleModalVisibility(false)}
				hideClose
			>
				<SVGIcon
					className="w-16 h-auto stroke-yellow justify-self-center"
					icon="Error"
				/>
				<Text className="text-center text-lg text-black">
					{modalAlert}
				</Text>
				<Text className="text-center text-gray-label">
					{modalDescription}
				</Text>
				<Button
					theme="light"
					className="w-40 justify-self-center"
					onClick={() => handleModalVisibility(false)}
				>
					{t('misc.close')}
				</Button>
			</Modal>
			<Modal
				className="flex flex-col items-center justify-center gap-2 w-fit px-8 py-3 bg-gray-bg"
				isModalVisible={isLoading}
				onClose={() => {
					return;
				}}
				hideClose
			>
				<Text className="text-center">{t('misc.sending_data')}...</Text>
				<ClipLoader
					className="justify-self-center"
					color="#6e6e6e"
					size={20}
				/>
			</Modal>
		</>
	);
};

export default SearchBar;
